import { useEffect, useState } from "react";
import { ServiceInfoDc, TableInfoDc } from "@evergis/api";

import { getLayerInfo } from "../../../../../evergis/core/layer/getLayerInfo";
import { isReadable } from "../../../../../evergis/core/permissions";
import { extractResourceName } from "../../../../../utils/resourceName";
import { useLayerDeps } from "./useLayerDeps";
import { useDefaultScaleRange } from "../../../../../hooks/style/useDefaultScaleRange";
import { layer as layerApi } from "../../../../../resources/layers";
import { LayerInfo } from "../../../../../interfaces/resourceInfo";
import { LayerStructureInfo } from "../../types";

export const useLayerInfo = (layerName?: string) => {
  const [layerStructure, setLayerStructure] = useState<LayerStructureInfo | undefined>(undefined);
  const [structureLoading, setStructureLoading] = useState<boolean>(true);
  const { data: layerInfo, refetch } = layerApi.use({ input: layerName, waitInput: true, immediate: false });
  const { tables, layers, loading: depsLoading } = useLayerDeps(layerName);
  const defaultScaleRange = useDefaultScaleRange();

  useEffect(() => {
    refetch().then(() => setStructureLoading(false));
  }, [refetch]);

  useEffect(() => {
    if (!tables || !layers || !layerInfo?.alias || depsLoading || structureLoading) {
      return undefined;
    }

    const filteredTables: TableInfoDc[] = (tables && tables.filter(table => isReadable(table.permissions))) || [];

    setLayerStructure({
      alias: layerInfo.alias,
      tables: filteredTables.map((table: TableInfoDc) => {
        return {
          ...table,
          layers: (Array.isArray(layers)
            ? layers.reduce<LayerInfo[]>((acc, layer): LayerInfo[] => {
                const currLayerInfo = getLayerInfo(layer, defaultScaleRange);

                if (
                  !isReadable(layer.permissions) ||
                  extractResourceName(currLayerInfo.tableName) !== extractResourceName(table.name)
                ) {
                  return acc;
                }

                return [...acc, currLayerInfo];
              }, [])
            : []) as ServiceInfoDc[],
        };
      }),
    });
  }, [tables, layers, layerInfo, depsLoading, structureLoading, defaultScaleRange]);

  return { layerStructure, loading: structureLoading || depsLoading, layerInfo };
};
